import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatEUR, formatCrypto, ASSET_ICONS } from "../lib/api";
import { Brand, AssetGlyph } from "./Brand";
import { OrderStageList } from "./Stepper";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import { Copy, ExternalLink, Home, CheckCircle2, Share2, Printer } from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import AnimatedNumber from "./AnimatedNumber";

export default function StatusScreen() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [searchParams] = useSearchParams();
  const stripeSession = searchParams.get("session_id");
  const [pollingStripe, setPollingStripe] = useState(!!stripeSession);
  const firedConfettiRef = useRef(false);

  // Poll order status
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const r = await api.get(`/v1/orders/${id}`);
        if (mounted) setOrder(r.data);
      } catch (e) { /* ignore */ }
    };
    load();
    const intv = setInterval(load, 2500);
    return () => { mounted = false; clearInterval(intv); };
  }, [id]);

  // Confetti on completion
  useEffect(() => {
    if (!order || order.status !== "completed" || firedConfettiRef.current) return;
    firedConfettiRef.current = true;
    const colors = ["#059669", "#0A3B2C", "#22C55E", "#10B981"];
    confetti({ particleCount: 70, spread: 65, startVelocity: 35, origin: { y: 0.5 }, colors, scalar: 0.9 });
    setTimeout(() => confetti({ particleCount: 40, spread: 90, origin: { y: 0.4, x: 0.3 }, colors, scalar: 0.7 }), 220);
    setTimeout(() => confetti({ particleCount: 40, spread: 90, origin: { y: 0.4, x: 0.7 }, colors, scalar: 0.7 }), 380);
  }, [order]);

  // Poll Stripe checkout status if returning from card payment
  useEffect(() => {
    if (!stripeSession) return;
    let attempts = 0;
    const poll = async () => {
      if (attempts >= 8) { setPollingStripe(false); return; }
      try {
        const r = await api.get(`/payments/v1/checkout/status/${stripeSession}`);
        if (r.data.payment_status === "paid") {
          setPollingStripe(false);
          toast.success("Payment confirmed");
          return;
        }
        if (r.data.status === "expired") { setPollingStripe(false); return; }
      } catch (e) { /* keep polling */ }
      attempts++;
      setTimeout(poll, 2000);
    };
    poll();
  }, [stripeSession]);

  if (!order) {
    return (
      <div className="xch-app-bg pb-10">
        <header className="xch-ink sticky top-0 z-20">
          <div className="max-w-md mx-auto px-4 py-3"><Brand size={28} theme="dark" /></div>
        </header>
        <div className="max-w-md mx-auto px-4 pt-6">
          <div className="xch-card p-6 space-y-4" data-testid="status-loading">
            <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
              <Skeleton className="w-12 h-12 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-6 w-40" />
                <Skeleton className="h-3 w-28" />
              </div>
            </div>
            {[1,2,3,4,5].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="w-7 h-7 rounded-full" />
                <Skeleton className="h-4 flex-1 max-w-[180px]" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const complete = order.status === "completed";
  const underReview = order.status === "under_review";
  const refundPending = order.status === "refund_pending";

  const explorerUrl = (() => {
    const h = order.on_chain_tx_hash;
    if (!h) return null;
    const n = order.network || "";
    if (n.includes("Tron"))       return `https://tronscan.org/#/transaction/${h}`;
    if (n === "Solana")           return `https://solscan.io/tx/${h}`;
    if (n === "Polygon")          return `https://polygonscan.com/tx/${h}`;
    if (n === "Base")             return `https://basescan.org/tx/${h}`;
    if (n === "Arbitrum")         return `https://arbiscan.io/tx/${h}`;
    if (n === "Optimism")         return `https://optimistic.etherscan.io/tx/${h}`;
    if (n.includes("Avalanche"))  return `https://snowtrace.io/tx/${h}`;
    if (n.includes("BNB"))        return `https://bscscan.com/tx/${h}`;
    if (n === "Bitcoin")          return `https://mempool.space/tx/${h}`;
    return `https://etherscan.io/tx/${h}`;  // ERC-20 default
  })();

  const shareBuy = () => {
    const text = `I just bought ${formatCrypto(order.receive_amount)} ${order.asset} on Xchangely.io 🚀`;
    if (navigator.share) {
      navigator.share({ title: "Xchangely buy", text, url: window.location.href }).catch(()=>{});
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(`${text} ${window.location.href}`)
        .then(() => toast.success("Share text copied to clipboard"))
        .catch(() => toast.error("Couldn't copy to clipboard"));
    } else {
      toast.error("Sharing not supported on this device");
    }
  };

  return (
    <div className="xch-app-bg pb-10">
      <header className="xch-ink sticky top-0 z-20">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Brand size={28} theme="dark" />
          <Button variant="ghost" size="sm" onClick={() => navigate("/account")} className="text-white hover:text-white hover:bg-white/10" data-testid="status-home-btn">
            <Home className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 pt-6">
        <div className="xch-card p-6 md:p-7 space-y-6" data-testid="status-card">
          {/* Summary */}
          <div className="flex items-center gap-3 pb-5 border-b border-slate-100">
            <AssetGlyph symbol={order.asset} color={ASSET_ICONS[order.asset]} size={48} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Order</div>
              <div className="font-display text-xl font-bold text-primary tabular-nums" data-testid="status-amount">
                <AnimatedNumber value={order.receive_amount} format={(v) => formatCrypto(v)} /> {order.asset}
              </div>
              <div className="text-xs text-slate-500">
                for {formatEUR(order.fiat_amount)} · via {order.payment_method.toUpperCase()}
              </div>
            </div>
          </div>

          {underReview && (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 space-y-1.5" data-testid="status-under-review">
              <div className="flex items-center gap-2 font-display font-bold text-amber-900">
                <CheckCircle2 className="w-4 h-4" /> Your order is under review
              </div>
              <p className="text-xs text-amber-900/85 leading-relaxed">
                For transactions over €5,000, our payments partner runs an additional review (Source-of-Funds + crypto risk).
                This typically completes within 24 hours. We'll notify you the moment it's released — no action needed from you.
                <strong className="block mt-1">Your money is safe. Nothing has moved on-chain yet.</strong>
              </p>
            </div>
          )}

          {refundPending && (
            <div className="rounded-2xl bg-sky-50 border border-sky-200 p-4 space-y-1.5" data-testid="status-refund-pending">
              <div className="font-display font-bold text-sky-900">Refund in progress</div>
              <p className="text-xs text-sky-900/85">
                We've initiated a refund to your original bank account via SEPA Secure. Funds should arrive within
                1–2 business days.
              </p>
            </div>
          )}

          {complete && (
            <div className="bg-accent/5 border border-accent/20 rounded-2xl p-4 flex items-center gap-3" data-testid="status-complete-banner">
              <CheckCircle2 className="w-8 h-8 text-accent shrink-0" />
              <div className="flex-1">
                <div className="font-display font-bold text-primary">Crypto sent successfully</div>
                <div className="text-xs text-slate-600">Your {order.asset} has arrived in your wallet.</div>
              </div>
              <Button variant="ghost" size="sm" onClick={shareBuy} data-testid="status-share-btn">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          )}

          {pollingStripe && (
            <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600 flex items-center gap-2">
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              Confirming your card payment with Stripe…
            </div>
          )}

          <OrderStageList stages={order.stages || []} />

          {order.on_chain_tx_hash && (
            <div className="bg-slate-50 rounded-2xl p-3 space-y-1.5" data-testid="status-tx-hash">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">On-chain transaction</div>
              <div className="flex items-center gap-2">
                <div className="font-mono text-xs text-primary truncate flex-1">{order.on_chain_tx_hash}</div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(order.on_chain_tx_hash)
                      .then(() => toast.success("Tx copied"))
                      .catch(() => toast.error("Couldn't copy"));
                  }}
                  className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
                  data-testid="status-tx-copy"
                ><Copy className="w-3.5 h-3.5 text-slate-500" /></button>
                <a
                  href={explorerUrl} target="_blank" rel="noreferrer"
                  className="p-1.5 rounded-lg hover:bg-slate-200 transition-colors"
                  data-testid="status-tx-explorer"
                ><ExternalLink className="w-3.5 h-3.5 text-slate-500" /></a>
              </div>
            </div>
          )}

          <div className="bg-slate-50 rounded-xl p-3 text-xs text-slate-600 flex items-center justify-between" data-testid="status-wallet-summary">
            <span className="text-slate-500">Sent to</span>
            <span className="font-mono text-primary font-semibold truncate ml-2">
              {order.wallet_address.slice(0, 8)}…{order.wallet_address.slice(-6)}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {complete && (
              <Button
                onClick={() => navigate(`/account/orders/${order.id}/receipt`)}
                variant="outline"
                className="rounded-xl h-11 font-display font-semibold gap-1.5"
                data-testid="status-view-receipt-btn"
              >
                <Printer className="w-4 h-4" /> Print receipt
              </Button>
            )}
            <Button
              onClick={() => navigate("/account/orders")}
              variant="outline"
              className="rounded-xl h-11 font-display font-semibold"
              data-testid="status-view-orders-btn"
            >
              View all orders
            </Button>
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
}

const Footer = () => (
  <div className="text-center text-[10px] text-slate-400 mt-6 leading-relaxed">
    Xchangely.io
  </div>
);
